import React from "react";
import styles from './RootLayout.module.scss'
import AppBar from '@mui/material/AppBar'
import { createTheme, IconButton, Link, Menu, MenuItem, styled, Theme, ThemeProvider, Toolbar, Typography } from "@mui/material"
import Box from "@mui/material/Box"
import AccountCircle from '@mui/icons-material/AccountCircle'
import MoreIcon from '@mui/icons-material/MoreVert'
import logo from '@/assets/img/bidgely.png'
import { EventEditDialog, MODE } from "features/eventManagement/editEventDialog/EventEditDialog"
import { useGlobalConfig } from "context/GlobalContext"
import { EVENT_MANAGEMENT_STRINGS } from "types/translations/EventManagementStrings"
import { BaseConfigs } from "types/configs/BaseConfigs"
import { ScheduledEvent } from "types"
import { BIDGELY_TYPOGRAPHY } from "./Theme/typography"
import { BglOutlinedButton, BglPrimaryButton } from "components/button/BglButtons"
import { EVInstantaneousStopEventDialog } from "features/eventManagement/EVInstantaneousStopEvent/EVInstantaneousStopEventDialog";
import { EV_INST_EVENT_STRINGS } from "types/translations/EVLoadManagementStrings";
import { useNavigate } from "react-router-dom";

interface RootLayoutProps {
  children: React.ReactNode
}

const BglAppBar = styled(AppBar)(({ theme }) => ({
  width: '100%',
  backgroundColor : 'white',
  color: 'black'
}))

export const RootLayout = ({children} : RootLayoutProps) => {
  
  const { rc } = useGlobalConfig();
  const { translations, config, session } = rc;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null)
  const [openSE, setOpenSE] = React.useState(false)
  const [openIE, setOpenIE] = React.useState(false)
  const navigate = useNavigate();

  const realTimeDashboardSupported = JSON.parse(config.getConfig("EVENT_MANAGEMENT.EV.REALTIME_DASHBOARD"));

  let theme : Theme = createTheme({
    bglPalette : rc.theme.getTheme(),
    components: {
      // Name of the component
      MuiButtonBase: {
        defaultProps: {
          // The props to change the default for.
          disableRipple: true, // No more ripple, on the whole application 💣!
        },
      },
    },
  });

  theme = createTheme(theme, {
    palette : {
      text: {
        primary : theme.bglPalette.foreground[500],
        secondary : theme.bglPalette.secondary[500]
      }
    },
    typography: {
      fontFamily: [
        'Roboto',
        'Montserrat',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      fontSize: 14,
      color: theme.bglPalette.foreground[500],
      ...BIDGELY_TYPOGRAPHY,
    }
  });
  const handleSEClose = (event : any, reason ?: string) => {
    if(event){
      rc.analytics.trackEvent(event, {reason});
    }
    if (reason && reason === 'backdropClick') return
    setOpenSE(false)
  }

  const handleIEClose = (event : any, reason ?: string) => {
    if(event){
      rc.analytics.trackEvent(event, {reason});
    }
    if (reason && reason === 'backdropClick') return
    setOpenIE(false)
  }

  const handeScheduleEvent = () => {
    rc.analytics.trackEvent('SCHEDULE_EVENT_BTN_CLICKED', {});
    setOpenSE(true)
  }

  const handeInstantanouesEVEvent = () => {
    rc.analytics.trackEvent('INSTANTANEOUS_EV_STOP_EVENT_BTN_CLICKED', {});
    setOpenIE(true)
  }

  const isMenuOpen = Boolean(anchorEl)
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  const handleLogout = () => {
    session.logoutUser();
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    handleMobileMenuClose()
  }

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget)
  }


  const menuId = 'primary-search-account-menu'
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleLogout}>
        <Typography
        sx={{typography : {md : "bglBody1"}}}>
          Logout
        </Typography>
      </MenuItem>
      {/* <MenuItem onClick={handleMenuClose}>My account</MenuItem> */}
    </Menu>
  )

  const mobileMenuId = 'primary-search-account-menu-mobile'
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {realTimeDashboardSupported && <MenuItem onClick={handeInstantanouesEVEvent}>
        <IconButton
          size="large"
          aria-label="instantly schedule"
          aria-controls="instant-schedule-menu"
          aria-haspopup="true"
          color="primary">
        </IconButton>
        <p>{translations.getTranslation(EV_INST_EVENT_STRINGS.BTN.INSTANT_STOP_EV_CHARGING)}</p>
      </MenuItem>}
      <MenuItem onClick={handeScheduleEvent}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="primary">
        </IconButton>
        <p>{translations.getTranslation(EVENT_MANAGEMENT_STRINGS.ACTIONS.SCHEDULE)}</p>
      </MenuItem>
    </Menu>
  )

  return (
    <ThemeProvider theme={theme}>
      <div className={styles.root}>
        <BglAppBar position="sticky">
          <Toolbar>
            <img className={styles.logo} src={logo} alt="Bidgely" />
            <Typography
              noWrap
              component="div"
              sx={{ typography: { md: "bglSubtitle1", xs: "body2" } }}
            >
              {translations.getTranslation(EVENT_MANAGEMENT_STRINGS.TITLE)}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: { xs: "none", md: "flex" } }}>

              {realTimeDashboardSupported && (
                <BglOutlinedButton
                  onClick={handeInstantanouesEVEvent}
                  sx={{ margin: "10px 5px" }}
                >
                  <Typography
                    component="div"
                    sx={{ typography: { md: "bglBody2" } }}
                  >
                    {translations.getTranslation(
                      EV_INST_EVENT_STRINGS.BTN.INSTANT_STOP_EV_CHARGING
                    )}
                  </Typography>
                </BglOutlinedButton>
              )}

              <BglPrimaryButton
                onClick={handeScheduleEvent}
                sx={{ margin: "10px 0px" }}
              >
                <Typography
                  component="div"
                  sx={{ typography: { md: "bglBody2" } }}
                >
                  +{" "}
                  {translations.getTranslation(
                    EVENT_MANAGEMENT_STRINGS.POPUP.BTN.SCHEDULE
                  )}
                </Typography>
              </BglPrimaryButton>

              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="primary"
              >
                <AccountCircle sx={{ fontSize: "35px" }} />
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="primary"
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </BglAppBar>
        {renderMobileMenu}
        {renderMenu}
        <main className={styles.mainCont}>{children}</main>
        <div className={styles.footerCont}>
          <Typography
            color="primary"
            component="div"
            sx={{
              textDecoration: "underline",
              marginBottom: "10px",
              typography: { md: "bglBody1" },
            }}
          >
            <Link
              href={`mailto:${config.getConfig(
                BaseConfigs.CONTACT_SUPPORT_EMAIL
              )}?Subject=Event scheduling support needed`}
            >
              Contact Support
            </Link>
          </Typography>
          <Typography sx={{ typography: { md: "bglBody2" } }}>
            @2023, Bidgely Inc.
          </Typography>
        </div>
        {openSE && 
          <EventEditDialog
            mode={MODE.ADD}
            open={openSE}
            handleClose={handleSEClose}
          />}
        {openIE && 
          <EVInstantaneousStopEventDialog
            open={openIE}
            handleClose={handleIEClose}
          />}
      </div>
    </ThemeProvider>
  );
}